"use client";

/**
 * Payment section ("2. Paiement sécurisé") + CTA.
 *
 * The Stripe flow itself is byte-identical to the legacy implementation:
 *  - `createPaymentMethod` from CardElement
 *  - `createPaymentIntent` Redux thunk (3DS branch handled inline)
 *  - `createOrder` thunk on success
 *  - `confirmCardPayment` for `requires_action`
 *  - same coins / wire-transfer side flows
 *
 * Only the visual chrome changed — the form now lives inside the
 * page's gd-card and shares the same submit CTA at the bottom.
 */
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import Link from "next/link";
import { useParams } from "next/navigation";
import { Trans } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/app/lib/hooks";
import { createPaymentIntent } from "@/app/lib/reducers/stripeReducer";
import { createOrder, verifyOrderByPaymentIntent } from "@/app/lib/reducers/ordersReducer";
import { isPayLaterEligible } from "@/app/lib/payLaterEligibility";
import { usePayLaterOpenToAllPro } from "@/app/lib/usePayLaterOpenToAllPro";
import { usePayLaterHoursBefore } from "@/app/lib/usePayLaterHoursBefore";
import api from "@/app/lib/reducers/api";
import { verifyTokenSuccess } from "@/app/lib/reducers/authReducer";
import { useRouter } from "next/navigation";
import type { Route } from "next";
import { useTranslation } from "react-i18next";
import { lineTotalFor } from "@/utils/pricingUtils";

// Card element styled to match the gdx design system (burgundy ink for
// the typed digits, coral red for invalid input — matches the rest of
// the form's tone).
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#0F1A14",
      fontFamily: '"Inter", "Manrope", -apple-system, BlinkMacSystemFont, sans-serif',
      fontSmoothing: "antialiased",
      fontSize: "15px",
      "::placeholder": { color: "#97A89E" },
    },
    invalid: { color: "#C13B5C", iconColor: "#C13B5C" },
  },
  hidePostalCode: true,
};

interface CheckoutFormProps {
  cartItems: any[];
  selectedCurrency: string;
  discountUuid: string;
  coinsBalance: number;
  coinsToUse: number;
  coinsValueEUR: number;
  coinsValueCurrency: number;
  amountDueEUR: number;
  amountDueCurrency: number;
  discountedTotalEUR: number;
  subtotalCurrency: number;
  discountedTotalCurrency: number;
  currencyRate: number;
  /** Au moins une ligne du panier n'a pas de tarif : le total affiché est faux. */
  cartPriceUnavailable?: boolean;
  /** Devis du serveur pas encore arrive : aucun paiement possible. */
  quotePending?: boolean;
  /** Le serveur a refuse un montant perime : redemander le devis. */
  onPriceChanged?: () => void;
  onCoinsSpent: (remainingCoins: number) => void;
}

const SectionHeader: FC<{ number: string; title: string; hint?: string }> = ({
  number,
  title,
  hint,
}) => (
  <div style={{ marginBottom: 14 }}>
    <div className="flex items-center" style={{ gap: 12, marginBottom: 6 }}>
      <span
        aria-hidden="true"
        className="grid place-items-center"
        style={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          background: "var(--g-burgundy)",
          color: "white",
          fontSize: 12,
          fontWeight: 800,
          flex: "0 0 auto",
        }}
      >
        {number}
      </span>
      <h3 className="font-bold" style={{ fontSize: 17, color: "var(--g-burgundy)" }}>
        {title}
      </h3>
    </div>
    {hint && (
      <p style={{ fontSize: 12, color: "var(--g-ink-500)", marginLeft: 38 }}>{hint}</p>
    )}
  </div>
);

const CheckoutForm: FC<CheckoutFormProps> = ({
  cartItems,
  selectedCurrency,
  discountUuid,
  coinsBalance,
  coinsToUse,
  coinsValueEUR,
  coinsValueCurrency,
  amountDueEUR,
  amountDueCurrency,
  discountedTotalEUR,
  subtotalCurrency,
  discountedTotalCurrency,
  currencyRate,
  cartPriceUnavailable = false,
  quotePending = false,
  onPriceChanged,
  onCoinsSpent,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { t } = useTranslation("guydeez");
  const { lang, currency } = useParams<{ lang: string; currency: string }>();

  const connectedUser = useAppSelector((s) => s.auth.user);

  const [cardHolderName, setCardHolderName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Once the card has actually been charged, the form is LOCKED: any later failure
  // (order creation, network) must never let the user pay again → no duplicate charge.
  const [paymentLocked, setPaymentLocked] = useState(false);
  const chargedPiRef = useRef<string | null>(null);

  // Stripe's <CardElement> mounts an <iframe> internally. Recent
  // versions ship it with a default title, but some Lighthouse audits
  // still flag it as untitled when the framework strips the attribute.
  // Setting the title defensively guarantees a 100/100 a11y score even
  // on Stripe SDK versions where the default is missing.
  const cardElementWrapperRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!stripe || !elements) return;
    const TITLE = t("checkout_card_iframe_title", {
      defaultValue: "Champ de saisie de carte bancaire sécurisé",
    }) as string;
    // Stripe takes a tick to mount the iframe — retry briefly until
    // it appears, then stop.
    let attempts = 0;
    const setIframeTitle = () => {
      const root = cardElementWrapperRef.current;
      if (!root) return false;
      const iframes = root.querySelectorAll("iframe");
      if (iframes.length === 0) return false;
      iframes.forEach((f) => {
        if (!f.getAttribute("title")) f.setAttribute("title", TITLE);
      });
      return true;
    };
    if (setIframeTitle()) return;
    const interval = window.setInterval(() => {
      attempts++;
      if (setIframeTitle() || attempts > 20) {
        window.clearInterval(interval);
      }
    }, 100);
    return () => window.clearInterval(interval);
  }, [stripe, elements, t]);

  const effectiveRate = currencyRate || 1;
  const coinsNeededForFull = Math.max(0, Math.round(discountedTotalEUR * 100));
  const coinsSelectionCovers = amountDueEUR <= 0.0001 && coinsToUse > 0;
  // « Rien a payer » doit se MERITER : une remise ou des coins qui couvrent le
  // total. Un panier dont le prix n'a pas pu etre charge vaut 0 par accident, et
  // ouvrait alors la voie de confirmation sans paiement. Tant que le prix est
  // inconnu, aucune des deux voies n'est proposee.
  const noPaymentDue = !cartPriceUnavailable && discountedTotalEUR <= 0.0001;
  const coinsCanCoverFull =
    !cartPriceUnavailable && coinsBalance >= coinsNeededForFull && coinsNeededForFull > 0;
  const requiresCardPayment = !cartPriceUnavailable && amountDueCurrency > 0.009;
  const allowCoinCompletion =
    !cartPriceUnavailable &&
    !requiresCardPayment &&
    (noPaymentDue || coinsSelectionCovers || coinsCanCoverFull);

  const updateLocalUserCoins = useCallback(
    (nextValue: number) => {
      if (!connectedUser) return;
      const nextUser = { ...connectedUser, guydeezCoin: nextValue } as any;
      try {
        const persisted = JSON.parse(localStorage.getItem("user") || "{}") || {};
        localStorage.setItem(
          "user",
          JSON.stringify({ ...persisted, user: nextUser, data: nextUser })
        );
      } catch (err) {
        console.warn("Failed to persist user coins", err);
      }
      dispatch(verifyTokenSuccess(nextUser));
      onCoinsSpent(nextValue);
    },
    [connectedUser, dispatch, onCoinsSpent]
  );

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    setErrorMessage(null);

    if (!stripe || !elements) {
      setErrorMessage(t("checkout_stripe_not_loaded"));
      return;
    }
    if (!connectedUser?.email) {
      setErrorMessage(
        t("checkout_login_required", "You need to be logged in to complete this payment.")
      );
      return;
    }
    if (!requiresCardPayment) {
      setErrorMessage(t("checkout_no_card_needed", "No card payment is required."));
      return;
    }
    // Rail mensuel : il n'y a plus de champ carte, donc plus rien a soumettre
    // ici. On peut quand meme y arriver — une touche Entree dans un champ de la
    // section « Vos informations » vaut un envoi de formulaire. Sans ce garde,
    // le parcours carte partait avec un CardElement absent et affichait « erreur
    // de carte » a quelqu'un a qui on vient d'ecrire qu'aucune carte n'est
    // demandee.
    //
    // On ne declenche PAS la commande mensuelle a la place : elle a son bouton,
    // lui seul est protege contre le double envoi, et ce fichier engage de
    // l'argent reel.
    if (monthlyOffered) return;

    setIsLoading(true);
    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        setErrorMessage(t("checkout_card_details_error"));
        setIsLoading(false);
        return;
      }

      const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: { name: cardHolderName, email: connectedUser.email },
      });
      if (pmError || !paymentMethod) {
        setErrorMessage(pmError?.message || t("checkout_payment_method_creation_failed"));
        setIsLoading(false);
        return;
      }

      const transformedCartItems = cartItems.map((item) => {
        const nb = (item.travellers?.adults || 0) + (item.travellers?.children || 0);
        // Jamais price * effectif : une ligne "Group" porte le total du groupe.
        const lineEUR = lineTotalFor(item.pricing, nb) ?? 0;
        const convertedPrice = parseFloat((lineEUR * effectiveRate).toFixed(2));
        return {
          id: item.uuid || "unknown",
          name: item.tour?.name || "Unknown Tour",
          price: convertedPrice,
          quantity: 1,
        };
      });

      const idempotencyKey =
        typeof crypto !== "undefined" && (crypto as any).randomUUID
          ? (crypto as any).randomUUID()
          : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const piResp: any = await dispatch(
        createPaymentIntent(
          transformedCartItems,
          paymentMethod.id,
          connectedUser.email,
          `${connectedUser.lastName || ""} ${connectedUser.firstName || ""}`.trim(),
          parseFloat(coinsValueCurrency.toFixed(2)),
          idempotencyKey,
          // Le devis du serveur, celui affiche sur le bouton : le serveur debite ce
          // montant et refuse s'il ne correspond plus a son calcul.
          parseFloat(amountDueCurrency.toFixed(2)),
          coinsToUse
        )
      );

      // Le prix a change depuis l'affichage : RIEN n'a ete debite. On redemande le devis,
      // le nouveau montant s'affiche, et le client paie a nouveau s'il est d'accord.
      if (piResp?.status === "error" && piResp?.code === "price_changed") {
        onPriceChanged?.();
        setErrorMessage(
          t(
            "checkout_price_changed",
            "The price of your booking has changed. Please check the new amount, then pay again."
          )
        );
        setIsLoading(false);
        return;
      }

      // Charge outcome UNKNOWN (network/timeout): the card may already be charged.
      // Lock the form — retrying here would create a SECOND charge.
      if (piResp?.status === "error" && piResp?.ambiguous) {
        setPaymentLocked(true);
        setErrorMessage(
          t(
            "checkout_payment_status_unknown",
            "We couldn't confirm your payment status. Please DO NOT try again — contact support before retrying to avoid a double charge."
          )
        );
        return;
      }

      // Server duplicate-charge guard: this cart is already paid/booked → no charge
      // was made. Lock the form and show why (prevents the retry-storm at the source).
      if (piResp?.status === "duplicate") {
        setPaymentLocked(true);
        setErrorMessage(
          piResp.message ||
          t(
            "checkout_duplicate_payment",
            "A payment for this booking was already received. Please do not pay again — contact support if your booking isn't confirmed."
          )
        );
        return;
      }

      if (piResp?.status === "succeeded" && piResp?.paymentIntentId) {
        chargedPiRef.current = piResp.paymentIntentId; // card charged — lock on any later error
        const { orderUuid, remainingCoins } = await dispatch(
          createOrder(
            amountDueCurrency.toFixed(2),
            selectedCurrency,
            {
              paymentIntent: piResp.paymentIntentId,
              paymentProvider: "stripe",
              paymentIntentId: piResp.paymentIntentId,
            } as any,
            cartItems.map((i) => i.uuid).filter(Boolean) as string[],
            discountUuid,
            {
              coinsUsed: coinsToUse,
              coinsValueEUR,
              coinsValueCurrency,
              originalPrice: subtotalCurrency,
            }
          )
        );
        if (!orderUuid) throw new Error("Order reference missing.");
        setErrorMessage(null);
        try { elements.getElement(CardElement)?.clear(); } catch {}
        const updatedCoins =
          typeof remainingCoins === "number"
            ? remainingCoins
            : Math.max(0, coinsBalance - coinsToUse);
        if (updatedCoins !== coinsBalance) updateLocalUserCoins(updatedCoins);
        else onCoinsSpent(updatedCoins);
        router.push(`/order-complete?order=${orderUuid}` as Route);
        return;
      }

      if (piResp?.status === "requires_action" && piResp?.clientSecret) {
        const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
          piResp.clientSecret,
          { payment_method: paymentMethod.id }
        );
        if (confirmError) {
          if (confirmError.code === "authentication_required") {
            setErrorMessage(t("checkout_3ds_authentication_failed"));
          } else if (confirmError.code === "card_declined") {
            setErrorMessage(t("checkout_card_declined"));
          } else {
            setErrorMessage(confirmError.message || t("checkout_payment_failed_try_again"));
          }
          setIsLoading(false);
          return;
        }
        if (!paymentIntent || paymentIntent.status !== "succeeded") {
          setErrorMessage(t("checkout_payment_failed_try_again"));
          setIsLoading(false);
          return;
        }

        chargedPiRef.current = paymentIntent.id; // 3DS succeeded → card charged, lock on any later error
        const { orderUuid, remainingCoins } = await dispatch(
          createOrder(
            amountDueCurrency.toFixed(2),
            selectedCurrency,
            {
              paymentIntent: paymentIntent.id,
              paymentProvider: "stripe",
              paymentIntentId: paymentIntent.id,
            } as any,
            cartItems.map((i) => i.uuid).filter(Boolean) as string[],
            discountUuid,
            {
              coinsUsed: coinsToUse,
              coinsValueEUR,
              coinsValueCurrency,
              originalPrice: subtotalCurrency,
            }
          )
        );
        if (!orderUuid) throw new Error("Order reference missing.");
        try { elements.getElement(CardElement)?.clear(); } catch {}
        const updatedCoins =
          typeof remainingCoins === "number"
            ? remainingCoins
            : Math.max(0, coinsBalance - coinsToUse);
        if (updatedCoins !== coinsBalance) updateLocalUserCoins(updatedCoins);
        else onCoinsSpent(updatedCoins);
        router.push(`/order-complete?order=${orderUuid}` as Route);
        return;
      }

      setErrorMessage(t("checkout_payment_failed_try_again"));
    } catch (err) {
      console.error("Payment error:", err);
      if (chargedPiRef.current) {
        // The card was ALREADY charged. Before showing a failure, VERIFY whether the
        // booking actually went through (a lost/slow create-order response or a 409
        // duplicate must NOT be shown as "failed" when the order really exists).
        const recoveredUuid = await dispatch(
          verifyOrderByPaymentIntent(chargedPiRef.current)
        );
        if (recoveredUuid) {
          setErrorMessage(null);
          try { elements.getElement(CardElement)?.clear(); } catch {}
          router.push(`/order-complete?order=${recoveredUuid}` as Route);
          return;
        }
        // Charged but genuinely no order → lock (no re-charge) and route to support.
        setPaymentLocked(true);
        setErrorMessage(
          t(
            "checkout_payment_received_finalize",
            "Your payment was received but we couldn't finalize the booking. Please DO NOT pay again — contact support with reference "
          ) + chargedPiRef.current
        );
      } else {
        setErrorMessage(t("checkout_error_occured_during_payment"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Wire transfer was removed from checkout on 2026-08-25.
  // In 13 months it produced 16 orders, of which exactly ONE was ever confirmed as
  // received — the other 15 are still sitting undecided in the moderation queue, the
  // oldest from July 2025, each holding a cart in 'waiting-payment' (so no guide was
  // ever dispatched for them). It was not a payment channel, it was a way to strand a
  // booking. The server refuses new wire orders too; this is not a client-side hide.
  //
  // What deliberately stays elsewhere: /order-pending, the proof-of-payment upload, the
  // admin moderation screen and the fzco bank details. The 15 open orders still need to
  // be resolved, and B2B MONTHLY INVOICES are settled by transfer — that is a different
  // flow from paying a single booking at checkout.

  // ── B2B "Book Now, Pay Later" ────────────────────────────────────────────────
  // Mirrors the server gate, tolerant of both role shapes (numeric in some paths, a
  // label in others — see (guide-dashboard-pages)/(components)/Nav.tsx).
  // This is COSMETIC: it decides which button to show, it authorises nothing. Every
  // check is re-run server-side, which is the only place that decides.
  // Single source of truth for the client-side check — see payLaterEligibility.ts, and
  // keep it in sync with PAY_LATER_ROLES on the backend.
  // `payLaterOpenToAllPro` vient de l'option du back-office : elle leve la
  // restriction aux roles internes, des deux cotes a la fois.
  const payLaterOpenToAllPro = usePayLaterOpenToAllPro();
  // Le delai de debit annonce au client. Sert de repli quand l'apercu n'a pas
  // (ou plus) de valeur a donner : le refus « trop tard » arrive justement dans
  // les cas ou l'apercu est absent ou perime.
  const optionHoursBefore = usePayLaterHoursBefore();
  // Comment CE compte règle ses réservations. Le serveur refuse toute commande
  // dont le mode ne correspond pas au sien (billing_mode_mismatch) : le tunnel
  // doit donc le lui demander au lieu de supposer « paiement différé ».
  // Un échec de lecture laisse `per_booking`, c'est-à-dire le parcours ordinaire.
  const [settlementMode, setSettlementMode] = useState<"per_booking" | "monthly">("per_booking");

  useEffect(() => {
    if (!connectedUser?.uuid) return;
    let alive = true;
    api
      .get("/api/billing/settlement-mode")
      .then(({ data }) => {
        if (alive && data?.billingMode === "monthly") setSettlementMode("monthly");
      })
      .catch(() => {
        // 403 = le rail B2B est fermé pour ce compte ; il n'a de toute façon
        // accès à aucun des deux modes.
      });
    return () => {
      alive = false;
    };
  }, [connectedUser?.uuid]);

  const b2bEligible = isPayLaterEligible(connectedUser, payLaterOpenToAllPro);
  // Un compte mensuel ne passe PAS par le paiement différé : pas de carte à
  // engager, pas de débit à 72 h, la réservation part sur la facture du mois.
  const isMonthlyAccount = settlementMode === "monthly";
  const canPayLater =
    !isMonthlyAccount && b2bEligible && coinsToUse <= 0 && requiresCardPayment;
  const canBookMonthly =
    isMonthlyAccount && b2bEligible && coinsToUse <= 0 && requiresCardPayment;

  // Whether the tour is far enough away to defer is NOT computed here. It depends on
  // each tour city's timezone, and duplicating that arithmetic client-side is exactly
  // how the two sides drift apart. The server decides and answers 409 pay_now_required,
  // which drops the customer back onto the ordinary card form.
  const [payLaterInfo, setPayLaterInfo] = useState<{ dueAt?: string; last4?: string | null } | null>(null);
  // DEUX refus distincts, qui ne se reprennent pas de la meme facon :
  //  - `railClosed` : le rail B2B est ferme pour CE compte (option coupee, pas dans
  //    la liste blanche, suspendu). Rien de ce que le client modifie ici ne le rouvre,
  //    donc il reste pose jusqu'au rechargement de la page.
  //  - `payNowRequired` : la visite etait trop proche du delai. C'est une propriete de
  //    la DATE, pas du compte — et la date se modifie depuis « Modifier la reservation ».
  //    Il doit donc se lever des que la reservation bouge, sinon une visite repoussee
  //    reste privee de paiement differe pour le reste de la session.
  const [railClosed, setRailClosed] = useState(false);
  const [payNowRequired, setPayNowRequired] = useState(false);

  const [isRegisteringCard, setIsRegisteringCard] = useState(false);

  // ── Choix « payer maintenant » / « payer plus tard » ────────────────────────
  // Proposer un choix suppose de pouvoir l'expliquer : « nous débiterons votre
  // carte le samedi 10 octobre » veut dire qu'il faut la date AVANT de réserver.
  // Elle se déduit de l'heure de début de la visite dans le fuseau de sa ville
  // moins un délai réglable — un calcul qui, refait ici, finirait par diverger de
  // celui du serveur. D'où cet aperçu : le serveur répond avec les mêmes
  // fonctions que la commande, et dit aussi quand le différé n'est plus possible.
  //
  // Il ne fait autorité sur rien. La commande revérifie tout.
  const [payLaterPreview, setPayLaterPreview] = useState<{
    deferrable: boolean;
    dueAt?: string;
    // Le délai vient de l'option OPT-PAY-LATER-HOURS-BEFORE, réglable depuis le
    // back-office. Il est repris tel que le serveur l'a lu pour CE calcul : le
    // réécrire en dur ici ferait mentir le bouton dès que l'option change.
    hoursBefore?: number;
  } | null>(null);
  // « maintenant » par défaut : le différé est une option qu'on choisit, jamais
  // un mode dans lequel on se retrouve sans l'avoir demandé.
  const [payMode, setPayMode] = useState<"now" | "later">("now");

  // `cartItems` porte des OBJETS panier ; l'API attend des identifiants, comme
  // partout ailleurs dans ce fichier.
  const cartUuids = useMemo(
    () => (cartItems || []).map((i: any) => i?.uuid).filter(Boolean) as string[],
    [cartItems],
  );
  // La clé de dépendance de l'aperçu porte AUSSI la date et l'heure de chaque
  // ligne, pas seulement les identifiants.
  //
  // POURQUOI. « Modifier la réservation » écrit dans la MÊME ligne de panier
  // (`cart.update()` côté serveur) : l'uuid ne bouge pas. Une clé bâtie sur les
  // seuls uuids ne changeait donc jamais, l'effet ne se rejouait pas, et l'aperçu
  // restait celui de l'ANCIENNE date. Une visite prévue demain puis repoussée de
  // trois jours n'obtenait jamais « payer plus tard » alors qu'elle y a droit ;
  // symétriquement, une visite avancée à demain continuait de le proposer
  // jusqu'au 409 de la commande.
  //
  // Seul l'instant de départ compte pour le différé (`computeSchedule` prend la
  // ligne la plus proche) : ni la durée, ni la langue, ni l'effectif n'entrent ici.
  const bookingKey = useMemo(
    () =>
      (cartItems || [])
        .map(
          (i: any) =>
            `${i?.uuid || ""}@${i?.datetime?.date || ""}T${i?.datetime?.time || ""}`,
        )
        .join(","),
    [cartItems],
  );

  // Une réservation qui change annule le refus lié à l'ancienne date, et le message
  // qui l'expliquait. `railClosed` n'est pas touché : il ne dépend pas de la date.
  // Le mode de paiement retombe sur « maintenant » — le différé se choisit, il ne
  // se retrouve jamais coché tout seul après une modification.
  const previousBookingKey = useRef(bookingKey);
  useEffect(() => {
    if (previousBookingKey.current === bookingKey) return;
    previousBookingKey.current = bookingKey;
    setPayNowRequired(false);
    setPayMode("now");
    setErrorMessage("");
  }, [bookingKey]);

  useEffect(() => {
    if (!canPayLater || !cartUuids.length) {
      setPayLaterPreview(null);
      return;
    }
    let alive = true;
    api
      .post("/api/billing/pay-later-preview", { cartItems: cartUuids })
      .then(({ data }) => {
        if (!alive) return;
        setPayLaterPreview({
          deferrable: !!data?.deferrable,
          dueAt: data?.dueAt,
          hoursBefore: Number(data?.hoursBefore),
        });
        // Une visite devenue trop proche pendant que la page est ouverte ne doit
        // pas laisser « payer plus tard » coché : la commande serait refusée.
        if (!data?.deferrable) setPayMode("now");
      })
      .catch(() => {
        // Aperçu indisponible : on referme l'option et on laisse le parcours
        // ordinaire. Ne jamais bloquer un paiement pour un défaut d'affichage.
        if (alive) setPayLaterPreview(null);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPayLater, bookingKey]);

  // Le choix n'est offert que si le serveur confirme que le différé est encore
  // possible pour CE panier.
  const payLaterOffered =
    canPayLater && !railClosed && !payNowRequired && !!payLaterPreview?.deferrable;
  const deferSelected = payLaterOffered && payMode === "later";

  // « Possible » et « offert » ne sont pas la meme chose : `canBookMonthly` dit
  // que le compte est en facturation mensuelle, pas que le rail est ouvert a cet
  // instant. Les trois conditions etaient recopiees a chaque usage, et elles ont
  // fini par diverger : le bouton mensuel exigeait `!railClosed`, le bouton carte
  // se contentait de `!canBookMonthly`. Un compte mensuel dont le rail se ferme
  // se retrouvait donc SANS aucun bouton — ni mensuel, ni carte.
  const monthlyOffered = canBookMonthly && !railClosed && !payNowRequired;

  // Le délai réel, tel que lu par le serveur dans OPT-PAY-LATER-HOURS-BEFORE.
  // Repli sur un libellé sans chiffre si l'aperçu ne l'a pas donné : mieux vaut
  // une phrase vague qu'un « 72 h » écrit en dur qui deviendrait faux le jour où
  // l'option change.
  // L'apercu d'abord — c'est la valeur que le serveur a reellement utilisee pour CE
  // calcul. L'option ensuite, pour les moments ou l'apercu n'existe pas.
  const previewHours = Number(payLaterPreview?.hoursBefore);
  const deferHours =
    Number.isFinite(previewHours) && previewHours > 0 ? previewHours : (optionHoursBefore ?? NaN);
  const hasDeferHours = Number.isFinite(deferHours) && deferHours > 0;

  /**
   * Le ou les mois de facturation de ce panier, en toutes lettres.
   *
   * C'est le mois de la VISITE qui decide, pas celui de la reservation :
   * `selectBillableCarts` (b2bInvoiceGenerator.service.js) borne la periode sur
   * `carts.datetime` entre le 1er du mois et le 1er du suivant. Une visite
   * reservee en septembre pour octobre part donc sur la facture d'octobre.
   *
   * Un panier peut porter plusieurs visites a cheval sur deux mois : elles
   * tomberont sur DEUX factures. Annoncer un seul mois serait faux, on les
   * nomme donc tous, dans l'ordre.
   */
  const monthlyInvoiceMonths = useMemo(() => {
    const byMonth = new Map<string, Date>();
    (cartItems || []).forEach((item: any) => {
      // `datetime.date` est une chaine « YYYY-MM-DD ». On ne la donne pas a
      // `new Date()` : selon le fuseau, le 1er du mois bascule sur le mois
      // precedent, et la mention annoncerait la mauvaise facture.
      const parts = String(item?.datetime?.date || "").match(/^(\d{4})-(\d{2})/);
      if (!parts) return;
      const key = `${parts[1]}-${parts[2]}`;
      if (!byMonth.has(key)) {
        byMonth.set(key, new Date(Number(parts[1]), Number(parts[2]) - 1, 1));
      }
    });
    const tag = (lang || "fr_FR").replace("_", "-");
    // `Array.from` et non un spread : la cible TypeScript du projet n'autorise
    // pas le parcours direct d'un iterateur de Map (TS2802).
    return Array.from(byMonth.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, date]) => {
        try {
          return new Intl.DateTimeFormat(tag, { month: "long", year: "numeric" }).format(date);
        } catch {
          // Etiquette de langue inconnue : le mois reste lisible plutot que de
          // faire tomber tout le bloc de paiement.
          return new Intl.DateTimeFormat("fr-FR", { month: "long", year: "numeric" }).format(date);
        }
      });
  }, [cartItems, lang]);

  const chargeDateLabel = useMemo(() => {
    const raw = payLaterPreview?.dueAt;
    if (!raw) return "";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    try {
      return d.toLocaleDateString((lang || "fr_FR").replace("_", "-"), {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return d.toLocaleDateString("fr-FR");
    }
  }, [payLaterPreview?.dueAt, lang]);

  /**
   * Make sure the account has a card on file, registering the one typed here if not.
   *
   * The card field on this page is the SAME element as for an immediate payment — what
   * changes is what we do with it: confirmCardSetup (registration, no money moves)
   * instead of confirmCardPayment. Doing it here rather than sending the customer to
   * their account page first is the whole point: they are already looking at a card
   * form, at the moment they decided to book.
   *
   * Returns an error message to display, or null on success.
   */
  const ensureCardOnFile = async (): Promise<string | null> => {
    let hasDefault = false;
    try {
      const { data } = await api.get("/api/billing/payment-methods");
      hasDefault = (data?.cards || []).some((c: any) => c.isDefault);
    } catch {
      // 403 here means the rail is closed for this account; the order call below will
      // say so properly. Not having a card is not an error yet.
    }
    if (hasDefault) return null;

    if (!stripe || !elements) return t("checkout_pay_later_failed", { defaultValue: "La réservation en paiement différé a échoué." });
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return t("checkout_pay_later_card_required", { defaultValue: "Enregistrez d'abord une carte." });

    setIsRegisteringCard(true);
    try {
      const { data } = await api.post("/api/billing/setup-intent", {});
      const result = await stripe.confirmCardSetup(data.clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: { name: cardHolderName, email: connectedUser?.email },
        },
      });
      if (result.error) return result.error.message || t("account_cards_error", { defaultValue: "La carte n'a pas pu être enregistrée." });

      // Only the SetupIntent id travels: the server reads the card back from Stripe, so
      // a client can never attach a payment method it does not own.
      await api.post("/api/billing/payment-methods", { setupIntentId: data.setupIntentId });
      return null;
    } catch (err: any) {
      return err?.response?.data?.message || t("account_cards_error", { defaultValue: "La carte n'a pas pu être enregistrée." });
    } finally {
      setIsRegisteringCard(false);
    }
  };

  /**
   * Les deux rails B2B passent par ici. Ils ne different que sur deux points :
   * le mode envoye au serveur, et la carte — une facture de fin de mois n'en
   * engage aucune, le serveur saute d'ailleurs l'exigence de carte et le
   * plafond d'encours dans ce cas.
   */
  const handleB2bOrder = async (mode: "pay_later" | "monthly_invoice") => {
    try {
      setIsLoading(true);
      setErrorMessage("");

      if (mode === "pay_later") {
        // Register the card first. A deferred booking is refused outright without one, so
        // there is no point creating anything before this succeeds.
        const cardError = await ensureCardOnFile();
        if (cardError) {
          setErrorMessage(cardError);
          return;
        }
      }

      const res: any = await dispatch(
        createOrder(
          amountDueCurrency.toFixed(2),
          selectedCurrency,
          { paymentIntent: mode, paymentProvider: mode },
          cartItems.map((item) => item.uuid).filter(Boolean) as string[],
          discountUuid,
          { originalPrice: subtotalCurrency }
        )
      );
      if (!res?.orderUuid) throw new Error("Order reference missing.");
      setPayLaterInfo({ dueAt: res?.settlement?.dueAt, last4: res?.settlement?.cardLast4 });
      router.push(`/order-complete?order=${res.orderUuid}` as Route);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === "price_changed") {
        // Le prix a change depuis l'affichage : rien n'est reserve ni programme. Nouveau devis.
        onPriceChanged?.();
        setErrorMessage(
          t(
            "checkout_price_changed",
            "The price of your booking has changed. Please check the new amount, then pay again."
          )
        );
      } else if (code === "pay_later_disabled" || code === "not_eligible" || code === "not_in_allowlist" || code === "suspended") {
        // The rail is off (or this account is not allowed on it) — something the client
        // cannot know without asking, since the switch lives in the `options` table. Fall
        // back to the ordinary card form instead of leaving the customer on a button that
        // just failed: the booking is still perfectly payable, right now.
        setRailClosed(true);
        setErrorMessage("");
      } else if (code === "pay_now_required") {
        // La visite est dans la fenetre du delai (option OPT-PAY-LATER-HOURS-BEFORE) :
        // il n'y a plus rien a differer. Refus lie a la DATE, donc leve des que la
        // reservation est modifiee — voir l'effet sur `bookingKey`.
        setPayNowRequired(true);
        setErrorMessage(
          hasDeferHours
            ? t("checkout_pay_later_too_late_hours", {
                defaultValue:
                  "Cette visite a lieu dans moins de {{n}} h : le paiement est immédiat. Réglez par carte ci-dessous.",
                n: deferHours,
              })
            : t("checkout_pay_later_too_late", {
                defaultValue:
                  "Cette visite est trop proche pour le paiement différé : le paiement est immédiat. Réglez par carte ci-dessous.",
              })
        );
      } else if (code === "card_required") {
        setErrorMessage(
          t("checkout_pay_later_card_required", {
            defaultValue:
              "Enregistrez d'abord une carte depuis votre compte pour réserver en paiement différé.",
          })
        );
      } else {
        setErrorMessage(
          err?.response?.data?.message ||
            t("checkout_pay_later_failed", {
              defaultValue: "La réservation en paiement différé a échoué.",
            })
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteWithCoins = useCallback(async () => {
    if (!connectedUser?.uuid) {
      setErrorMessage(
        t("checkout_login_required", "You need to be logged in to complete this payment.")
      );
      return;
    }
    setIsLoading(true);
    setErrorMessage(null);

    const coinsForOrder = noPaymentDue
      ? 0
      : coinsSelectionCovers
      ? coinsToUse
      : Math.min(coinsBalance, coinsNeededForFull);
    const coinsValueForOrderEUR = coinsForOrder / 100;
    const coinsValueForOrderCurrency = parseFloat(
      (coinsValueForOrderEUR * effectiveRate).toFixed(2)
    );
    const amountAfterCoinsEUR = Math.max(0, discountedTotalEUR - coinsValueForOrderEUR);
    const amountAfterCoinsCurrency = Math.max(
      0,
      parseFloat((amountAfterCoinsEUR * effectiveRate).toFixed(2))
    );
    const provider =
      amountAfterCoinsCurrency <= 0
        ? coinsForOrder > 0
          ? "coins"
          : "free"
        : "stripe";

    if (!allowCoinCompletion || provider === "stripe") {
      setIsLoading(false);
      setErrorMessage(
        t("checkout_coins_not_enough", "YourGift Card do not cover the full amount.")
      );
      return;
    }

    try {
      const { orderUuid, remainingCoins } = await dispatch(
        createOrder(
          amountAfterCoinsCurrency.toFixed(2),
          selectedCurrency,
          { paymentProvider: provider },
          cartItems.map((item) => item.uuid).filter(Boolean) as string[],
          discountUuid,
          {
            coinsUsed: coinsForOrder,
            coinsValueEUR: coinsValueForOrderEUR,
            coinsValueCurrency: coinsValueForOrderCurrency,
            originalPrice: subtotalCurrency,
          }
        )
      );
      if (!orderUuid) throw new Error("Order reference missing.");
      const updatedCoins =
        typeof remainingCoins === "number"
          ? remainingCoins
          : Math.max(0, coinsBalance - coinsForOrder);
      if (updatedCoins !== coinsBalance) updateLocalUserCoins(updatedCoins);
      else onCoinsSpent(updatedCoins);
      router.push(`/order-complete?order=${orderUuid}` as Route);
    } catch (err: any) {
      console.error("Coin checkout failed", err);
      const backendMessage = err?.response?.data?.message || err?.message;
      setErrorMessage(backendMessage || t("checkout_error_occured_during_payment"));
    } finally {
      setIsLoading(false);
    }
  }, [
    allowCoinCompletion,
    connectedUser,
    cartItems,
    discountUuid,
    effectiveRate,
    noPaymentDue,
    coinsSelectionCovers,
    coinsToUse,
    coinsBalance,
    coinsNeededForFull,
    discountedTotalEUR,
    subtotalCurrency,
    t,
    dispatch,
    router,
    selectedCurrency,
    updateLocalUserCoins,
    onCoinsSpent,
  ]);

  const totalLabel = requiresCardPayment
    ? `${amountDueCurrency.toFixed(2)} ${selectedCurrency}`
    : "";

  return (
    <form onSubmit={handleSubmit} style={{ marginBottom: 0 }}>
      <SectionHeader
        number="2"
        title={t("checkout_payment_section_title", { defaultValue: "Paiement sécurisé" })}
      />

      <div style={{ marginLeft: 38 }}>
        {/* Le compte pro choisit QUAND il paie, au lieu de se voir imposer un
            mode. Avant, un client éligible voyait son bouton « payer » remplacé
            en silence par une réservation différée : même libellé, même montant,
            rien qui dise que la carte ne serait débitée que des semaines plus
            tard. Le choix est ici explicite, et la date de débit est annoncée
            avant de réserver, pas après.

            Le champ carte reste affiché dans les deux cas : sur « plus tard » il
            ne débite rien, il enregistre l'empreinte que le cron débitera. */}
        {payLaterOffered && (
          <div
            role="radiogroup"
            aria-label={t("checkout_when_to_pay", {
              defaultValue: "Quand souhaitez-vous payer ?",
            })}
            style={{ marginBottom: 18, display: "grid", gap: 10 }}
          >
            {([
              {
                mode: "now" as const,
                label: t("checkout_pay_now_option", { defaultValue: "Payer maintenant" }),
                hint: "",
              },
              {
                mode: "later" as const,
                label: t("checkout_pay_later_option", { defaultValue: "Payer plus tard" }),
                hint: t("checkout_pay_later_option_hint", {
                  defaultValue:
                    "Vous ne paierez rien aujourd'hui. Nous débiterons votre carte le {{date}}.",
                  date: chargeDateLabel,
                }) as string,
              },
            ]).map((opt) => {
              const selected = payMode === opt.mode;
              return (
                <label
                  key={opt.mode}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "12px 14px",
                    borderRadius: 12,
                    cursor: "pointer",
                    background: selected ? "var(--g-bg-soft)" : "transparent",
                    // Vert de l'action principale, celui du bouton de paiement :
                    // c'est la meme decision qu'on est en train de prendre.
                    border: `1px solid ${selected ? "var(--g-green-ada)" : "var(--g-line)"}`,
                  }}
                >
                  <input
                    type="radio"
                    name="checkout-pay-mode"
                    value={opt.mode}
                    checked={selected}
                    onChange={() => setPayMode(opt.mode)}
                    disabled={isLoading || quotePending || paymentLocked}
                    style={{
                      marginTop: 2,
                      flex: "0 0 auto",
                      // `color` ET `accent-color`, et ce n'est pas une ceinture
                      // de plus : @tailwindcss/forms met `appearance: none` sur
                      // les boutons radio, ce qui rend `accent-color` sans effet,
                      // puis dessine la pastille avec `currentColor` — d'ou le
                      // bleu #2563eb du greffon. C'est `color` qui la colore ici.
                      // `accent-color` reste utile le jour ou le greffon saute.
                      color: "var(--g-green-ada)",
                      accentColor: "var(--g-green-ada)",
                      // L'anneau exterieur, lui, n'est ni la bordure ni la
                      // pastille : c'est l'anneau de focus, que le greffon
                      // dessine en box-shadow a partir de `--tw-ring-color`,
                      // bleu lui aussi. On redefinit la variable sur l'element —
                      // une declaration en ligne l'emporte sur celle de la
                      // feuille de styles.
                      "--tw-ring-color": "var(--g-green-ada)",
                    } as React.CSSProperties}
                  />
                  <span style={{ display: "block" }}>
                    <span
                      className="font-bold"
                      style={{ fontSize: 14, color: "var(--g-burgundy)" }}
                    >
                      {opt.label}
                    </span>
                    {opt.hint && chargeDateLabel && (
                      <span
                        style={{
                          display: "block",
                          fontSize: 12,
                          color: "var(--g-ink-500)",
                          marginTop: 2,
                        }}
                      >
                        {opt.hint}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        )}

        {/* Pas de champ carte sur le rail mensuel : rien ne sera preleve, la
            reservation part sur la facture de fin de mois. Demander un numero de
            carte pour ne jamais s'en servir, c'est faire hesiter quelqu'un a qui
            on vient d'ecrire qu'aucune carte n'est demandee.
            Le paiement differe, lui, GARDE le champ : la carte y est enregistree
            pour etre debitee plus tard. */}
        {requiresCardPayment && !monthlyOffered && (
          <>
            {/* Card brand chips — purely visual to match the mock. The
                actual brand detection is handled by Stripe inside CardElement. */}
            <div style={{ marginBottom: 14 }}>
              <label className="gdx-label" id="checkout-card-number-label">
                {t("checkout_card_number", { defaultValue: "Numéro de carte" })}
              </label>
              <div
                className="gdx-input"
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px 16px",
                  height: "auto",
                  minHeight: 44,
                }}
                role="group"
                aria-labelledby="checkout-card-number-label"
              >
                <div ref={cardElementWrapperRef} style={{ flex: 1 }}>
                  <CardElement options={CARD_ELEMENT_OPTIONS} />
                </div>
                <div className="flex items-center" style={{ gap: 6, marginLeft: 12 }}>
                  <span
                    aria-hidden="true"
                    className="grid place-items-center"
                    style={{
                      width: 32,
                      height: 22,
                      background: "#1A1F71",
                      borderRadius: 4,
                      color: "white",
                      fontSize: 9,
                      fontWeight: 800,
                    }}
                  >
                    VISA
                  </span>
                  <span
                    aria-hidden="true"
                    style={{
                      width: 32,
                      height: 22,
                      background: "linear-gradient(90deg, #EB001B, #F79E1B)",
                      borderRadius: 4,
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label className="gdx-label">
                {t("checkout_card_holder_name", {
                  defaultValue: "Nom sur la carte",
                })}
              </label>
              <input
                className="gdx-input"
                value={cardHolderName}
                onChange={(e) => setCardHolderName(e.target.value.toUpperCase())}
                placeholder={
                  t("checkout_enter_card_holder_name", {
                    defaultValue: "CAMILLE BERTRAND",
                  }) as string
                }
                autoComplete="cc-name"
              />
            </div>

          </>
        )}

        {errorMessage && (
          <div
            role="alert"
            style={{
              marginTop: 12,
              padding: "10px 14px",
              borderRadius: 10,
              background: "rgba(193,59,92,.08)",
              border: "1px solid rgba(193,59,92,.25)",
              color: "var(--g-coral)",
              fontSize: 13,
            }}
          >
            {errorMessage}
          </div>
        )}

        {/* Stripe trust info */}
        <div
          className="flex items-start"
          style={{
            marginTop: 18,
            padding: 14,
            background: "var(--g-bg-soft)",
            borderRadius: 12,
            fontSize: 12,
            color: "var(--g-ink-500)",
            gap: 10,
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--g-green-ink)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ marginTop: 2, flex: "0 0 auto" }}
            aria-hidden="true"
          >
            <rect x="4" y="10" width="16" height="11" rx="2" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3" />
          </svg>
          <span>
            {/* Le paiement differe ne reprend PLUS la date de debit ici : elle est
                annoncee en tete de section, avec le nom du mode. La repeter a douze
                lignes d'intervalle donnait deux fois la meme phrase sur le meme ecran.
                La facturation mensuelle, elle, garde la sienne : il n'y a pas de champ
                carte au-dessus, donc ce bandeau est le seul endroit qui l'explique. */}
            {monthlyOffered
              ? t("checkout_monthly_trust", {
                  defaultValue:
                    "Aucune carte n'est demandée : cette réservation sera portée sur votre facture de fin de mois, réglée par virement.",
                })
              : t("checkout_stripe_trust", {
                  defaultValue:
                    "Vos infos de paiement sont chiffrées et traitées par Stripe. Guydeez ne stocke aucune donnée bancaire.",
                })}
          </span>
        </div>
      </div>

      {/* CTAs — sit at the bottom of the card, full-width */}
      <div style={{ marginTop: 28 }}>
        {/* Le prix d'au moins une ligne n'a pas pu etre charge. Aucun bouton
            n'est propose : ni paiement, ni confirmation sans paiement. Le dire
            explicitement vaut mieux qu'un ecran sans action, et evite surtout de
            laisser croire que la reservation est gratuite. */}
        {cartPriceUnavailable && (
          <div
            role="alert"
            style={{
              padding: "14px 16px",
              borderRadius: 10,
              border: "1px solid #fca5a5",
              background: "#fef2f2",
              color: "#991b1b",
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            {t("checkout_price_unavailable", {
              defaultValue:
                "Le prix de votre panier n'a pas pu être chargé. Rechargez la page avant de réserver — aucun paiement n'est possible tant que le montant est inconnu.",
            })}
          </div>
        )}

        {/* Ce bouton suit le choix fait plus haut, il ne le remplace plus.
            Auparavant l'éligibilité B2B suffisait à substituer la réservation
            différée au paiement immédiat, sans que le client l'ait demandé. Il
            n'apparaît donc que si « payer plus tard » est coché — et ce choix
            n'est lui-même proposé que si le serveur confirme que la visite est
            encore assez lointaine.

            Le champ carte au-dessus reste utile : il enregistre l'empreinte au
            lieu de débiter. */}
        {deferSelected && (
          <button
            type="button"
            className="gdx-btn primary xl block"
            onClick={() => handleB2bOrder("pay_later")}
            disabled={isLoading || quotePending || !stripe || !elements || paymentLocked}
            style={{
              width: "100%",
              // `.gdx-btn.xl` fixe une hauteur de 64 px. Ce libellé-ci porte le
              // montant ET le délai, et passe donc sur deux lignes sous 400 px :
              // a hauteur figee, le texte deborderait du bouton. On laisse celui-ci
              // grandir, sans toucher a la classe partagee.
              height: "auto",
              minHeight: 64,
              paddingTop: 14,
              paddingBottom: 14,
              lineHeight: 1.3,
              textAlign: "center",
            }}
          >
            {/* Le cadenas reste celui du paiement immédiat : la saisie carte est
                aussi protégée ici, elle enregistre une empreinte.

                Le LIBELLÉ, lui, n'est plus le même. Les deux boutons ont d'abord
                été rendus volontairement indiscernables, au motif que
                l'engagement était identique et que seule la date du débit
                changeait. Cela se tenait tant que le mode était subi. Maintenant
                qu'il se choisit juste au-dessus, un bouton « Confirmer et payer »
                sous l'option « Payer plus tard » contredit le choix qu'on vient
                de faire. Il dit donc le montant ET le fait qu'il partira plus
                tard ; la date exacte reste dans l'option, pour ne pas l'écrire
                deux fois. */}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="4" y="10" width="16" height="11" rx="2" />
              <path d="M8 10V7a4 4 0 0 1 8 0v3" />
            </svg>
            {isRegisteringCard
              ? t("checkout_pay_later_saving_card", {
                  defaultValue: "Enregistrement de votre carte…",
                })
              : isLoading
                ? t("checkout_processing")
                : hasDeferHours
                  ? t("checkout_confirm_pay_later_hours", {
                      defaultValue:
                        "Confirmer — {{amount}} débités {{hours}} h avant le début de la visite",
                      amount: totalLabel,
                      hours: deferHours,
                    })
                  : t("checkout_confirm_pay_later", {
                      defaultValue: "Confirmer — {{amount}} débités plus tard",
                      amount: totalLabel,
                    })}
          </button>
        )}

        {/* Facturation mensuelle : la reservation est confirmee tout de suite et
            part sur la facture de fin de mois. Aucune carte n'est engagee — le
            serveur saute d'ailleurs l'exigence de carte et le plafond d'encours
            pour ce mode. */}
        {/* Sur QUELLE facture cette reservation tombera. « Fin de mois » ne
            suffisait pas : une visite reservee en septembre pour octobre part
            sur la facture d'octobre, emise en novembre. Le mois est donc calcule
            depuis la date de la visite, comme le fait le generateur de factures. */}
        {monthlyOffered && monthlyInvoiceMonths.length > 0 && (
          <p
            style={{
              margin: "0 0 12px",
              fontSize: 13,
              color: "var(--g-ink-700)",
              textAlign: "center",
            }}
          >
            {monthlyInvoiceMonths.length === 1
              ? t("checkout_monthly_invoice_month", {
                  defaultValue:
                    "Cette visite sera facturée sur votre facture de {{month}}.",
                  month: monthlyInvoiceMonths[0],
                })
              : t("checkout_monthly_invoice_months", {
                  defaultValue:
                    "Ces visites seront facturées sur vos factures de {{months}}.",
                  months: monthlyInvoiceMonths.join(", "),
                })}
          </p>
        )}
        {monthlyOffered && (
          <button
            type="button"
            className="gdx-btn primary xl block"
            onClick={() => handleB2bOrder("monthly_invoice")}
            disabled={isLoading || quotePending || paymentLocked}
            style={{ width: "100%" }}
          >
            {isLoading
              ? t("checkout_processing")
              : t("checkout_confirm_monthly", {
                  defaultValue: "Confirmer — Facturation mensuelle",
                })}
          </button>
        )}

        {/* Le paiement immédiat reste le parcours par défaut : il s'affiche dès
            que « payer plus tard » n'est pas coché, y compris pour un compte pro
            qui y aurait droit. */}
        {requiresCardPayment && !deferSelected && !monthlyOffered && (
          <button
            type="submit"
            className="gdx-btn primary xl block"
            disabled={isLoading || quotePending || !stripe || !elements || paymentLocked}
            style={{ width: "100%" }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="4" y="10" width="16" height="11" rx="2" />
              <path d="M8 10V7a4 4 0 0 1 8 0v3" />
            </svg>
            {isLoading
              ? t("checkout_processing")
              : t("checkout_confirm_and_pay", {
                  defaultValue: "Confirmer et payer {{amount}}",
                  amount: totalLabel,
                })}
          </button>
        )}

        {allowCoinCompletion && (
          <button
            type="button"
            className="gdx-btn primary xl block"
            onClick={handleCompleteWithCoins}
            disabled={isLoading || quotePending}
            style={{ width: "100%", marginTop: requiresCardPayment ? 10 : 0 }}
          >
            {isLoading
              ? t("checkout_processing")
              : t("checkout_complete_order", "Complete my order")}
          </button>
        )}

        <p
          style={{
            fontSize: 11,
            color: "var(--g-ink-500)",
            textAlign: "center",
            marginTop: 12,
          }}
        >
          {/* Inline links to the CMS-backed T&C page + the FAQ
              (cancellation policy). `<Trans>` swaps the `<terms>` and
              `<cancel>` tags inside the translated sentence with the
              real <Link> components so the copy stays editable in i18n
              JSON without manually concatenating strings around the
              links. Each locale's value must keep the two named tags
              for the substitution to work. */}
          <Trans
            i18nKey="checkout_terms_acceptance"
            ns="guydeez"
            defaults="En confirmant, vous acceptez les <terms>conditions générales</terms> et la <cancel>politique d'annulation</cancel>."
            components={{
              terms: (
                <Link
                  href={(`/${lang}/${currency}/terms-and-conditions`) as any}
                  style={{
                    color: "var(--g-burgundy)",
                    fontWeight: 600,
                    textDecoration: "underline",
                  }}
                />
              ),
              cancel: (
                <Link
                  href={(`/${lang}/${currency}/faq`) as any}
                  style={{
                    color: "var(--g-burgundy)",
                    fontWeight: 600,
                    textDecoration: "underline",
                  }}
                />
              ),
            }}
          />
        </p>
      </div>
    </form>
  );
};

export default CheckoutForm;
